import React from "react";
import { UserIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Card from "./ui/Card";
import type { BookingForm } from "../../types/booking";
import type { UseFormReturn } from "react-hook-form";
import { useGetHotelListQuery } from "../../services/common.service";

interface BookingPersonFormProps {
  form: UseFormReturn<BookingForm, unknown, unknown>;
}

const BookingPersonForm: React.FC<BookingPersonFormProps> = ({ form }) => {
  const {
    register,
    formState: { errors },
    watch,
  } = form;

  const { data: hotelData } = useGetHotelListQuery(
    "pagination=false&type=hotel&online_booking=Y&status=Active&sortBy=name",
  );
  const { data: agentData } = useGetHotelListQuery(
    "pagination=false&type=agent&online_booking=Y&status=Active",
  );
  const OTHER_LABEL =
    "- Other (if your accommodation is not in this list please write in the comments on the next page)";
  const hotelOptions = (hotelData?.items ?? []).map((hotel) => {
    const isOther = hotel.name === OTHER_LABEL;
    return {
      value: String(hotel.id),
      label: hotel.name,
      className: isOther ? "font-bold" : undefined,
    };
  });

  const agentOptions = (agentData?.items ?? []).length
    ? (agentData?.items ?? []).map((agent: { id: number; name: string }) => ({
        value: String(agent.id),
        label: agent.name,
      }))
    : [];

  const watchedValues = watch();

  return (
    <Card className="lg:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Booking Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Input
            {...register("bookingPerson.name")}
            label="Name of booking person"
            placeholder="Enter your full name"
            required
            error={errors.bookingPerson?.name?.message}
            icon={<UserIcon className="h-5 w-5 text-gray-400" />}
          />

          <Input
            {...register("bookingPerson.email")}
            type="email"
            label="Email"
            placeholder="your@email.com"
            required
            error={errors.bookingPerson?.email?.message}
            icon={<EnvelopeIcon className="h-5 w-5 text-gray-400" />}
          />

          <Input
            {...register("bookingPerson.confirmEmail")}
            type="email"
            label="Confirm Email"
            placeholder="Confirm your email"
            required
            onPaste={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
            autoComplete="off"
            error={errors.bookingPerson?.confirmEmail?.message}
            icon={<EnvelopeIcon className="h-5 w-5 text-gray-400" />}
          />
        </div>

        <div className="mb-6">
          <Select
            {...register("bookingPerson.hotelId")}
            label="Where are you staying"
            placeholder="Select your accommodation"
            isSearchable
            searchPlaceholder="Search hotels..."
            required
            options={hotelOptions}
            error={errors.bookingPerson?.hotelId?.message}
            value={watchedValues.bookingPerson?.hotelId || ""}
          />
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Travel Agent Information
            <span className="text-sm font-normal text-gray-600 block">
              Leave blank if you are booking directly with us
            </span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              {...register("bookingPerson.agentId")}
              label="Agent Name"
              placeholder="Select agent (optional)"
              isSearchable
              searchPlaceholder="Search agents..."
              options={agentOptions}
              value={watchedValues.bookingPerson?.agentId || ""}
            />

            <Input
              {...register("bookingPerson.referenceNo")}
              label="Reference Number"
              placeholder="Ref No (optional)"
              maxLength={20}
            />
          </div>
        </div>
      </motion.div>
    </Card>
  );
};

export default BookingPersonForm;
